import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type {
  FredrickLocalMarketItemSummaryV1,
  FredrickLocalMarketOverviewV1,
} from 'fredrick-local-repository';
import { StatusPill } from '../components/StatusPill';
import { LibraryNav } from '../components/library/LibraryNav';
import { ObservedSpellings } from '../components/library/ObservedSpellings';
import { marketItemHref, marketLibraryHref, parseMarketLibraryState } from '../lib/libraryRoutes';
import { formatIntegerMeso, formatPrice } from '../lib/marketFormat';
import { dateTime } from '../lib/format';
import { scheduleLocalMarketProcessing } from '../lib/marketProcessing';
import { localRepository } from '../lib/repository';

interface MarketIndexData {
  readonly overview: FredrickLocalMarketOverviewV1;
  readonly items: readonly FredrickLocalMarketItemSummaryV1[];
}

function ItemCard({ item }: { readonly item: FredrickLocalMarketItemSummaryV1 }) {
  const summary = item.latestSummary;
  return <Link className="market-item-card" to={marketItemHref(item.itemKey)}>
    <div className="market-item-title-row">
      <div>
        <h2>{item.itemName}</h2>
        <ObservedSpellings spellings={item.observedSpellings}/>
      </div>
      <div className="state-stack">
        <StatusPill tone={summary.completeness === 'complete' ? 'good' : 'warn'}>{summary.completeness}</StatusPill>
        <StatusPill tone={summary.groupingState === 'high-confidence' ? 'good' : 'warn'}>{summary.groupingState}</StatusPill>
      </div>
    </div>
    <div className="market-item-grid">
      <div><span>Observed</span><strong>{dateTime.format(summary.observedAt)}</strong></div>
      <div><span>Median price</span><strong>{summary.prices ? `${formatPrice(summary.prices.median)} meso` : 'No priced listings'}</strong></div>
      <div><span>Range</span><strong>{summary.prices ? `${formatPrice(summary.prices.min)}–${formatPrice(summary.prices.max)}` : '—'}</strong></div>
      <div><span>Listings / units</span><strong>{summary.listingCount} / {formatIntegerMeso(summary.totalUnits)}</strong></div>
      <div><span>History</span><strong>{item.searchCount} search{item.searchCount === 1 ? '' : 'es'}</strong></div>
    </div>
    {item.trustedLatest === null && <p className="uncertain-note">Visible only through the selected uncertainty filters; this does not define trusted latest market state.</p>}
  </Link>;
}

export function MarketLibraryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseMarketLibraryState(searchParams), [searchParams]);
  const [data, setData] = useState<MarketIndexData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => localRepository.subscribe(() => setRefreshToken((value) => value + 1)), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void Promise.all([
      localRepository.getMarketOverview(),
      localRepository.listMarketItems({
        query: state.query,
        includePartial: state.includePartial,
        includeAmbiguous: state.includeAmbiguous,
      }),
    ]).then(([overview, items]) => {
      if (cancelled) return;
      setData({ overview, items });
      setError(null);
    }).catch((value: unknown) => {
      if (!cancelled) setError(value instanceof Error ? value.message : 'Could not read local market data.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [state.query, state.includePartial, state.includeAmbiguous, refreshToken]);

  function update(next: Partial<typeof state>) {
    const merged = { ...state, ...next };
    const href = marketLibraryHref(merged);
    const query = href.split('?')[1] ?? '';
    setSearchParams(query, { replace: false });
  }

  const overview = data?.overview;
  const items = data?.items ?? [];
  const noQuery = state.query.trim().length === 0;
  const uncertainExists = (overview?.partialSearchCount ?? 0) > 0 || (overview?.ambiguousSearchCount ?? 0) > 0;

  return <div className="page-width content-page">
    <LibraryNav/>
    <div className="page-heading library-heading">
      <div>
        <p className="eyebrow">Offline market library</p>
        <h1>Local market items</h1>
        <p>Browse rebuildable market views derived entirely from screenshots stored in this browser. Prices are per item.</p>
      </div>
      <div className="heading-actions"><Link className="button primary" to="/import">Import screenshots</Link></div>
    </div>

    <section className="panel market-controls" aria-label="Market filters">
      <label className="search-field"><span>Search items</span><input value={state.query} onChange={(event) => update({ query: event.target.value })} placeholder="Item name"/></label>
      <label className="check-row"><input type="checkbox" checked={state.includePartial} onChange={(event) => update({ includePartial: event.target.checked })}/> Include partial searches</label>
      <label className="check-row"><input type="checkbox" checked={state.includeAmbiguous} onChange={(event) => update({ includeAmbiguous: event.target.checked })}/> Include ambiguous grouping</label>
    </section>

    {error && <div className="notice bad">{error} <button className="text-button" type="button" onClick={() => setRefreshToken((value) => value + 1)}>Retry</button></div>}

    {overview && <div className="market-overview">
      <div><strong>{overview.captureCount}</strong><span>captures</span></div>
      <div><strong>{overview.searchCount}</strong><span>grouped searches</span></div>
      <div><strong>{overview.trustedLatestItemCount}</strong><span>trusted latest items</span></div>
      <div><strong>{overview.pendingProcessingCount}</strong><span>pending processing</span></div>
    </div>}

    {loading && data === null ? <section className="empty-state panel"><div className="working centered-working"><span className="spinner"/>Opening local market views…</div></section>
      : overview?.captureCount === 0 ? <section className="empty-state panel"><span className="empty-mark">◎</span><h2>No local captures yet</h2><p>Import Owl screenshots to build the local market library.</p><Link className="button primary" to="/import">Import screenshots</Link></section>
        : overview && overview.pendingProcessingCount > 0 && overview.searchCount === 0 ? <section className="empty-state panel"><span className="empty-mark">↻</span><h2>Market views are being rebuilt</h2><p>Your captures are stored safely. Processing is durable and this page will refresh when the derived view commits.</p><button className="button secondary" type="button" onClick={scheduleLocalMarketProcessing}>Retry processing</button></section>
          : overview?.searchCount === 0 ? <section className="empty-state panel"><span className="empty-mark">—</span><h2>No processor-eligible searches</h2><p>The stored transcriptions do not yet contain all concrete item, page, seller, bundle, quantity, and price fields required by the market processor.</p><Link className="button secondary" to="/library/captures">Inspect captures</Link></section>
            : items.length === 0 && noQuery && uncertainExists && !state.includePartial && !state.includeAmbiguous ? <section className="empty-state panel"><span className="empty-mark">?</span><h2>Only uncertain searches are available</h2><p>Enable partial searches or ambiguous grouping above to inspect them without promoting them to trusted market state.</p></section>
              : items.length === 0 ? <section className="empty-state panel"><span className="empty-mark">⌕</span><h2>No items match</h2><p>Your current query and quality filters returned no local items.</p>{!noQuery && <button className="button secondary" type="button" onClick={() => update({ query: '' })}>Clear search</button>}</section>
                : <section className="market-item-list" aria-label="Local market items">{items.map((item) => <ItemCard key={item.itemKey} item={item}/>)}</section>}
  </div>;
}
